import { Link } from 'react-router-dom';
import { ArrowRight, Bell, CheckCircle2, CircleDashed, FolderOpen, ListChecks, TrendingUp } from 'lucide-react';
import { ProgressBar, StatusBadge, useTone } from './demarches/ui';

const formatDay = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** Colonne de droite : le score, ce qu'il mesure et comment l'améliorer. */
export const ScorePanel = ({ espace }) => {
  const tone = useTone();
  if (!espace) return <div className={`h-64 rounded-2xl animate-pulse ${tone.soft}`} />;
  const delta = espace.previous_score == null ? null : espace.score - espace.previous_score;
  return (
    <div data-testid="score-panel">
      <p className="text-[#81d8d0] text-sm font-semibold mb-1">Score administratif</p>
      <div className="flex items-end gap-3 mb-1">
        <p className="text-5xl font-semibold" data-testid="score-value">{espace.score} %</p>
        {delta ? (
          <span className={`text-sm font-semibold mb-2 ${delta > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
            <TrendingUp size={14} className="inline" /> {delta > 0 ? '+' : ''}{delta} pts
          </span>
        ) : null}
      </div>
      <p className={`text-sm leading-6 mb-4 ${tone.muted}`}>
        Il mesure à quel point ton dossier administratif est prêt : profil, pièces, démarches et impôts. Chaque action ci-dessous le fait monter.
      </p>
      <div className="space-y-3 mb-5">
        {espace.parts.map((p) => (
          <div key={p.id} title={p.explanation}>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-semibold">{p.label}</span>
              <span className={tone.muted}>{p.points} / {p.max}</span>
            </div>
            <ProgressBar value={(100 * p.points) / p.max} />
            <p className={`text-[11px] mt-1 ${tone.muted}`}>{p.explanation}</p>
          </div>
        ))}
      </div>
      {espace.actions.length > 0 && (
        <>
          <p className="text-sm font-semibold mb-2">Pour l'améliorer</p>
          <ul className="space-y-2" data-testid="score-actions">
            {espace.actions.map((a) => (
              <li key={a.label}>
                <Link to={a.path} className={`flex items-center justify-between gap-2 rounded-xl p-3 text-sm hover:ring-1 hover:ring-[#81d8d0] ${tone.soft}`}>
                  <span>{a.label}</span>
                  <span className="text-xs font-semibold text-[#81d8d0] whitespace-nowrap">+{a.points} pts</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

/** Bandeau « mon espace » : pièces, démarches en cours, rappels. */
export const EspaceOverview = ({ espace }) => {
  const tone = useTone();
  if (!espace) return null;
  const card = `border rounded-2xl p-5 ${tone.panel}`;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6" data-testid="espace-overview">
      <section className={card}>
        <h2 className="font-semibold mb-3 flex items-center gap-2"><ListChecks size={18} className="text-[#81d8d0]" /> Mes démarches</h2>
        {espace.dossiers.filter((d) => d.status !== 'abandonne').length === 0 ? (
          <p className={`text-sm mb-3 ${tone.muted}`}>Aucun dossier en cours. Skipya prépare ton aide au logement ou ta prime d'activité de A à Z.</p>
        ) : (
          <ul className="space-y-3 mb-3">
            {espace.dossiers.filter((d) => d.status !== 'abandonne').map((d) => (
              <li key={d.id}>
                <Link to={`/dashboard/demarches/${d.id}`} className="block">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold">{d.title}</span>
                    <span>{d.progress_percent} %</span>
                  </div>
                  <ProgressBar value={d.progress_percent} />
                  <div className="flex items-center justify-between mt-1.5 gap-2">
                    <StatusBadge status={d.status} />
                    <span className={`text-xs ${tone.muted} text-right`}>{d.next_action}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <Link to="/dashboard/demarches" className="text-sm font-semibold text-[#81d8d0] inline-flex items-center gap-1">
          Toutes les démarches <ArrowRight size={14} />
        </Link>
      </section>

      <section className={card}>
        <h2 className="font-semibold mb-3 flex items-center gap-2"><FolderOpen size={18} className="text-[#81d8d0]" /> Mes documents</h2>
        <ul className="space-y-1.5 text-sm mb-3">
          {espace.documents.present.map((d) => (
            <li key={d.type} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-500" /> {d.label}</li>
          ))}
          {espace.documents.missing.map((d) => (
            <li key={d.type}>
              <Link to={`/dashboard/documents?type=${d.type}`} className={`flex items-center gap-2 hover:text-[#81d8d0] ${tone.muted}`}>
                <CircleDashed size={16} /> {d.label} <span className="text-xs text-[#81d8d0]">ajouter</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className={card}>
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Bell size={18} className="text-[#81d8d0]" /> À venir</h2>
        {espace.reminders.length === 0 ? (
          <p className={`text-sm ${tone.muted}`}>Aucun rappel. Skipya en programme dès que tu déposes une demande (réponse, déclarations trimestrielles…).</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {espace.reminders.map((r) => (
              <li key={r.id} className="flex gap-3">
                <span className="text-xs font-semibold text-[#81d8d0] w-14 shrink-0">{formatDay(r.due_date)}</span>
                <span>{r.title}</span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/dashboard/tasks" className="mt-3 text-sm font-semibold text-[#81d8d0] inline-flex items-center gap-1">
          Mes actions <ArrowRight size={14} />
        </Link>
      </section>
    </div>
  );
};
